/**
 * 添付ファイルの型と入力検証（02 5.3.19 attachment・5.5・9.8）。
 * - 紐付先は linkedEntityType（formSubmission は公開フォームからだけ。社内の API-ATCH-01／02 では受け付けない）
 * - 大きさ・形式の上限の判定は service（FILE_003・FILE_004）。ここでは形（整数・文字数）だけを見る
 * - 名刺（customerContact）は business_card_side（front／back）が必須
 * - 応答に s3_object_key を出さない
 */
import { z } from 'zod';
import { LINKED_ENTITY_TYPE_VALUES } from '../enums/linked-entity-type';
import { MAX_FILE_NAME_LENGTH } from '../files/file-rules';
import { zCode, zEnum, zRequiredText, zSystemCode } from './common.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

export const BUSINESS_CARD_SIDE_VALUES = ['front', 'back'] as const;
export type BusinessCardSide = (typeof BUSINESS_CARD_SIDE_VALUES)[number];
export const BUSINESS_CARD_SIDE_LABELS: Readonly<Record<BusinessCardSide, string>> = { front: '表', back: '裏' };

/** 社内の画面から上げられる紐付先（formSubmission を除く） */
export const INTERNAL_LINKED_ENTITY_TYPE_VALUES = [
  'customer',
  'case',
  'transaction',
  'personnel',
  'contract',
  'customerContact',
] as const;

const zFileName = zRequiredText(MAX_FILE_NAME_LENGTH, 'MSG-V-032');
const zFileSize = z.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(1, 'MSG-V-020');

function requireCardSide<T extends { linked_entity_type: string; business_card_side?: string | null | undefined }>(
  value: T,
  context: z.RefinementCtx,
): void {
  const is_card = value.linked_entity_type === 'customerContact';
  if (is_card && !value.business_card_side) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['business_card_side'], message: 'MSG-V-020' });
  }
  if (!is_card && value.business_card_side) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['business_card_side'], message: 'MSG-V-020' });
  }
}

/** API-ATCH-01 添付用の署名付き POST の発行 */
export const AttachmentUploadUrlRequestSchema = z
  .object({
    linked_entity_type: zEnum(INTERNAL_LINKED_ENTITY_TYPE_VALUES),
    linked_entity_code: zCode,
    file_name: zFileName,
    file_size_bytes: zFileSize,
    mime_type: z.string({ message: 'MSG-V-031' }).trim().min(1, 'MSG-V-031').max(200, 'MSG-V-031'),
    business_card_side: zEnum(BUSINESS_CARD_SIDE_VALUES).nullable().optional(),
  })
  .strict()
  .superRefine(requireCardSide);

/** API-ATCH-02 アップロード完了の登録（API-ATCH-01 と同じ値） */
export const AttachmentCompleteSchema = z
  .object({
    attachment_code: zSystemCode,
    linked_entity_type: zEnum(INTERNAL_LINKED_ENTITY_TYPE_VALUES),
    linked_entity_code: zCode,
    file_name: zFileName,
    business_card_side: zEnum(BUSINESS_CARD_SIDE_VALUES).nullable().optional(),
  })
  .strict()
  .superRefine(requireCardSide);

/** 署名付き POST（ブラウザ → S3 へ直接。02 5.5） */
export const PresignedUploadSchema = z.object({
  attachment_code: z.string(),
  upload_url: z.string(),
  upload_fields: z.record(z.string()),
  expires_at: z.string(),
});

// 署名付き GET の応答（DownloadUrlSchema：download_url・expires_at・file_name）は⑦の job.schema.ts と同じ形のため、マージで job.schema.ts の 1 つにした

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const AttachmentSchema = z
  .object({
    attachment_code: z.string(),
    linked_entity_type: z.enum(LINKED_ENTITY_TYPE_VALUES),
    linked_entity_code: z.string(),
    file_name: z.string(),
    mime_type: z.string(),
    file_size_bytes: z.number().int(),
    uploaded_by: z.string().nullable(),
    uploaded_by_name: z.string().nullable(),
    /** 企業担当者の名刺として使われていれば（customer_contacts から判定） */
    business_card_side: z.enum(BUSINESS_CARD_SIDE_VALUES).nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const ATTACHMENT_LIST_SORT_VALUES = ['created_at', 'file_name', 'file_size_bytes'] as const;

/** API-ATCH-03 紐付先ごとのファイル一覧（新しい順が既定） */
export const AttachmentListQuerySchema = buildListQuerySchema(ATTACHMENT_LIST_SORT_VALUES, '-created_at').extend({
  linked_entity_type: zEnum(LINKED_ENTITY_TYPE_VALUES),
  linked_entity_code: zCode,
});

export type AttachmentUploadUrlRequest = z.infer<typeof AttachmentUploadUrlRequestSchema>;
export type AttachmentCompleteInput = z.infer<typeof AttachmentCompleteSchema>;
export type PresignedUpload = z.infer<typeof PresignedUploadSchema>;
export type Attachment = z.infer<typeof AttachmentSchema>;
export type AttachmentListQuery = z.infer<typeof AttachmentListQuerySchema>;
export type InternalLinkedEntityType = (typeof INTERNAL_LINKED_ENTITY_TYPE_VALUES)[number];
